/**
 * Historiku i një aseti — kush e ka pasur, kur, dhe çka i ndodhi.
 *
 * Dy pamje, jo një: **caktimet** (periudhat prej–deri, me gjendjen në të dyja
 * anët) dhe **ngjarjet** (regjistrimi, servisi, dëmtimi, ndalesa). Një listë e
 * vetme e përzier do t'i fshihte periudhat mes ngjarjeve teknike, dhe pikërisht
 * periudhat janë ajo që kërkon dikush kur pyet "kush e kishte më 12 maj?".
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Table, TableHead, TableBody, TableRow, TableCell, Stack, Divider, Alert, CircularProgress,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import StatusChip from '../common/StatusChip';
import {
  assetTitle, dateText, money, conditionLabel, conditionChip,
  statusLabel, statusChip, categoryLabel,
} from '../../config/assets';

function Field({ label, children }) {
  return (
    <Box sx={{ minWidth: 130 }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{children || '—'}</Typography>
    </Box>
  );
}

export default function AssetHistoryDialog({ open, loading, error, detail, onClose }) {
  const asset       = detail?.asset || null;
  const assignments = detail?.assignments || [];
  const events      = detail?.events || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: 'primary.soft', color: 'primary.main',
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <HistoryIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
            {asset ? assetTitle(asset) : 'Historiku i asetit'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {asset ? `${asset.assetCode} · ${categoryLabel(asset.category)}` : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={26} /></Box>
        )}
        {!loading && !!error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && asset && (
          <>
            {/* Identiteti dhe blerja — çka është pajisja */}
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Field label="Statusi">
                <StatusChip status={statusChip(asset.status)} label={statusLabel(asset.status)} size="sm" />
              </Field>
              <Field label="Gjendja">
                <StatusChip status={conditionChip(asset.condition)} label={conditionLabel(asset.condition)} size="sm" />
              </Field>
              <Field label="Numri serik">{asset.serialNumber}</Field>
              <Field label="Blerë më">{dateText(asset.purchaseDate)}</Field>
              <Field label="Vlera">{money(asset.purchaseValue, asset.currency)}</Field>
              <Field label="Garancioni">{dateText(asset.warrantyUntil)}</Field>
              <Field label="Furnitori">{asset.supplier}</Field>
              <Field label="Lokacioni">{asset.location}</Field>
            </Stack>

            {!!asset.notes && (
              <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                <Typography variant="body2">{asset.notes}</Typography>
              </Alert>
            )}

            <Divider sx={{ mb: 1.5 }} />

            {/* Caktimet — periudhat */}
            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.75 }}>
              Dorëzimet ({assignments.length})
            </Typography>
            {assignments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pajisja nuk i është dorëzuar askujt deri tani.
              </Typography>
            ) : (
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Punonjësi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Dorëzuar</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Kthyer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Gjendja dhënie → kthim</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Dëmi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        {/* Mungesa e punëtorit lexohet si mungesë, jo si rresht i prishur. */}
                        {a.workerName || <Typography component="span" variant="body2" color="text.disabled">Punëtor i fshirë</Typography>}
                      </TableCell>
                      <TableCell>{dateText(a.assignedOn)}</TableCell>
                      <TableCell>
                        {a.open
                          ? <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'info.onSoft' }}>Ende te punonjësi</Typography>
                          : dateText(a.returnedOn)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <StatusChip status={conditionChip(a.condition)} label={conditionLabel(a.condition)} size="sm" />
                          {!a.open && a.returnCondition && (
                            <>
                              <Typography component="span" color="text.disabled">→</Typography>
                              <StatusChip status={conditionChip(a.returnCondition)} label={conditionLabel(a.returnCondition)} size="sm" />
                            </>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {a.damageCost ? money(a.damageCost, asset.currency) : '—'}
                        {a.deductionId ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            ndalesë #{a.deductionId}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Divider sx={{ mb: 1.5 }} />

            {/* Ngjarjet — kronologjia */}
            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.75 }}>
              Kronologjia ({events.length})
            </Typography>
            {events.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Pa ngjarje të regjistruara.</Typography>
            ) : (
              <Stack spacing={0}>
                {events.map((e) => (
                  <Stack
                    key={e.id}
                    direction="row"
                    spacing={1.5}
                    sx={{ py: 0.85, borderBottom: '1px solid', borderColor: 'divider', alignItems: 'flex-start' }}
                  >
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 82, fontVariantNumeric: 'tabular-nums' }}>
                      {dateText(e.eventOn)}
                    </Typography>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                        {e.actionLabel}
                        {e.workerName ? <Typography component="span" sx={{ fontWeight: 500 }}> · {e.workerName}</Typography> : null}
                      </Typography>
                      {(e.fromStatus || e.toStatus) && e.fromStatus !== e.toStatus && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.35 }}>
                          {e.fromStatus && <StatusChip status={statusChip(e.fromStatus)} label={statusLabel(e.fromStatus)} size="sm" />}
                          {e.fromStatus && e.toStatus && <Typography component="span" color="text.disabled">→</Typography>}
                          {e.toStatus && <StatusChip status={statusChip(e.toStatus)} label={statusLabel(e.toStatus)} size="sm" />}
                        </Stack>
                      )}
                      {e.note && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{e.note}</Typography>
                      )}
                      {e.amount ? (
                        <Typography variant="caption" color="text.secondary">{money(e.amount, asset.currency)}</Typography>
                      ) : null}
                    </Box>
                    {e.actorEmail && (
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                        {e.actorEmail}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Mbyll</Button>
      </DialogActions>
    </Dialog>
  );
}
